import React from 'react'
import { router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { AlertTriangle, Clock, Loader2 } from 'lucide-react'
import { apiFetch } from '@/lib/csrf'
import { useToast } from '@/hooks/useToast'
import StripePaymentElement from '@/components/billing/StripePaymentElement'
import { type PaymentMethodSummary } from '@/components/billing/PaymentMethodCard'
import { type PaymentMethodRow } from '@/components/billing/PaymentMethodsSection'

// THE CARD DIALOG, AND WHY IT EXISTS AS ITS OWN COMPONENT (billing-payers §11.1).
//
// The first real production card-add went SILENT: 3-D Secure succeeded, the dialog closed, and the
// blind reload that followed raced the `setup_intent.succeeded` webhook, so the page it rendered
// was the page from before the card. The operator was left looking at a card surface that said
// "Add a card".
//
// Every path out of this dialog now ends in something the operator can see:
//
//   • The card saved  → the rows come back in the response, the dialog closes, a toast says so.
//   • The save failed → the dialog STAYS OPEN and swaps to a calm state carrying the server's own
//     sentence (or ours for a request that never reached a verdict), a Done button, and ONE delayed
//     reload as the webhook backstop. It never asks for the card again: the card is confirmed, and
//     re-entering it would tokenize a second one.
//   • A confirm in flight → the dialog cannot be closed. That promise spans the 3-D Secure
//     challenge, so closing underneath it would throw away a card the bank has already approved.
//
// It composes no billing copy beyond the two sentences below, does no money math, and assembles no
// URL — `confirmHref` is served whole by the backend.

// VERBATIM. Stripe told us the intent succeeded, so the card IS confirmed with the bank and the
// webhook will vault it; what failed is our own read-back. The sentence says exactly that, and
// asks for nothing.
export const CARD_PENDING_SENTENCE =
  'Your card was confirmed with your bank. It should appear here shortly.'

// How long we wait before the one backstop reload. Long enough for the webhook to land, short
// enough that an operator who is still looking at the page sees the card appear.
const BACKSTOP_RELOAD_MS = 4000

type Phase = 'form' | 'saving' | 'pending'

interface ConfirmResponse {
  payers?: PaymentMethodRow[]
  error?: string
}

export default function CardCaptureDialog({
  open,
  onOpenChange,
  payer,
  multiplePayers,
  accountCard,
  confirmHref,
  clientSecret,
  loadingSecret,
  secretError,
  stripePublishableKey,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Which payment method this card is for. Null on the single-payment-method flow. */
  payer: PaymentMethodRow | null
  multiplePayers: boolean
  accountCard: PaymentMethodSummary | null
  /** The synchronous vault endpoint, served whole by the backend. */
  confirmHref?: string
  clientSecret: string | null
  loadingSecret: boolean
  secretError: string | null
  stripePublishableKey: string | null
  /** The rows the vault answered with — the page renders the new card from these, never a reload. */
  onSaved: (payers: PaymentMethodRow[]) => void
}) {
  const { addToast } = useToast()
  const [phase, setPhase] = React.useState<Phase>('form')
  const [pendingSentence, setPendingSentence] = React.useState<string | null>(null)
  // True from the moment the operator presses "Save card" until Stripe resolves — which SPANS the
  // 3-D Secure challenge, and is exactly the window in which this dialog must refuse to close.
  const [processing, setProcessing] = React.useState(false)
  const backstop = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  // A re-opened dialog is a fresh capture: never inherit the last one's state.
  React.useEffect(() => {
    if (open) {
      setPhase('form')
      setPendingSentence(null)
      setProcessing(false)
    }
  }, [open])

  React.useEffect(
    () => () => {
      if (backstop.current) clearTimeout(backstop.current)
    },
    [],
  )

  const busy = processing || phase === 'saving'

  // The card is confirmed and we could not read it back. Say so, offer a way out, and let the
  // webhook finish the job — one reload, scheduled once.
  function holdForWebhook(sentence: string) {
    setPendingSentence(sentence)
    setPhase('pending')
    if (backstop.current) clearTimeout(backstop.current)
    backstop.current = setTimeout(() => router.reload(), BACKSTOP_RELOAD_MS)
  }

  // THE SETUP INTENT IS THE WHOLE MESSAGE. The server re-reads it from Stripe, checks it belongs to
  // this business, and vaults the card before it answers — so a 200 here means the card is saved,
  // not that a webhook is expected to save it.
  async function confirmCard(_paymentMethodId?: string, intentId?: string) {
    if (!confirmHref || !intentId) {
      holdForWebhook(CARD_PENDING_SENTENCE)
      return
    }

    setPhase('saving')
    try {
      const response = await apiFetch(confirmHref, {
        method: 'POST',
        body: JSON.stringify({ setup_intent_id: intentId }),
      })
      const data = (await response.json().catch(() => ({}))) as ConfirmResponse

      if (response.ok && Array.isArray(data.payers)) {
        onSaved(data.payers)
        onOpenChange(false)
        addToast({
          variant: 'success',
          title: 'Card saved',
          // Named only where naming it means something: on a business settling on several cards,
          // "Card saved" alone does not say which one just got it.
          description: multiplePayers && payer ? `${payer.name} now has a card on file.` : undefined,
        })
        return
      }

      // The server's sentence when it wrote one; ours when the answer carried none. Either way the
      // operator is told what is true, never left with a closed dialog and no card.
      holdForWebhook(typeof data.error === 'string' && data.error ? data.error : CARD_PENDING_SENTENCE)
    } catch {
      holdForWebhook(CARD_PENDING_SENTENCE)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // The data-loss guard: Esc, the close button and a click outside all arrive here, and none
        // of them may abandon a confirm the bank is still challenging.
        if (!next && busy) return
        onOpenChange(next)
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {phase === 'pending' ? 'Almost there' : cardModalTitle(payer, multiplePayers, accountCard)}
          </DialogTitle>
          <DialogDescription>
            {phase === 'pending'
              ? 'Nothing more to do here.'
              : multiplePayers && payer
                ? `This card pays the invoices for ${payer.name}. It is securely tokenized and we never see your full card number.`
                : 'Your card is securely tokenized for your recurring subscription. We never see your full card number.'}
          </DialogDescription>
        </DialogHeader>

        {phase === 'pending' ? (
          <>
            <DialogBody>
              <div
                role="status"
                className="flex items-start gap-2.5 rounded-lg border border-border/60 bg-white px-4 py-3 text-sm text-foreground shadow-sm"
              >
                <Clock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                <span>{pendingSentence}</span>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button onClick={() => onOpenChange(false)} className="rounded-lg">
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <DialogBody className="space-y-4">
            {loadingSecret && (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Preparing the secure card form…
              </div>
            )}
            {secretError && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{secretError}</span>
              </div>
            )}
            {phase === 'saving' && (
              <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving your card…
              </div>
            )}
            {phase === 'form' && clientSecret && stripePublishableKey && (
              <StripePaymentElement
                clientSecret={clientSecret}
                publishableKey={stripePublishableKey}
                mode="setup"
                submitLabel="Save card"
                onSuccess={confirmCard}
                onProcessingChange={setProcessing}
              />
            )}
          </DialogBody>
        )}
      </DialogContent>
    </Dialog>
  )
}

// The dialog names the payment method it is capturing for, but only on a business that has more
// than one — on every other one the wording stays exactly as it was.
export function cardModalTitle(
  payer: PaymentMethodRow | null,
  multiplePayers: boolean,
  accountCard: PaymentMethodSummary | null,
): string {
  if (multiplePayers && payer) {
    return payer.paymentMethod ? `Update the card for ${payer.name}` : `Add a card for ${payer.name}`
  }
  const card = payer?.paymentMethod ?? accountCard
  return card ? 'Update your card' : 'Add a card'
}
